/**
 * Small hook that wraps react-router-dom's useNavigate so every
 * navigation preserves the `?via=` (or `?key=`) parameter the embed
 * flow was launched with. Without this, the very first SPA navigate
 * call drops the key from the URL — which is fine as long as
 * sessionStorage is reliable, but a real-world failure (storage
 * cleared by an extension, sandboxed iframe with no storage, etc.)
 * leaves App.tsx with no way to recover the public_key and bounces
 * the user to the NotConfigured page mid-flow.
 *
 * Resolution order, picked once at first call site:
 *   1. current URL's `?key=` or `?via=`
 *   2. (fallback) sessionStorage `tn-embed-public-key`
 *
 * The resolved value gets re-attached to every navigate() so the
 * URL always carries it forward, even after multiple SPA hops.
 */

import { useCallback } from 'react';
import { useLocation, useNavigate, type NavigateOptions } from 'react-router-dom';

export function useFlowNavigate() {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(
    (to: string, options?: NavigateOptions) => {
      const params = new URLSearchParams(location.search);
      let key = params.get('via') || params.get('key');
      if (!key) {
        try { key = sessionStorage.getItem('tn-embed-public-key'); } catch { /* noop */ }
      }
      if (!key) {
        navigate(to, options);
        return;
      }

      // Parse `to` so we don't clobber any query string the caller
      // already attached.
      const [path, existingSearch] = to.split('?');
      const next = new URLSearchParams(existingSearch ?? '');
      if (!next.has('via') && !next.has('key')) {
        next.set('via', key);
      }
      const qs = next.toString();
      navigate(qs ? `${path}?${qs}` : path, options);
    },
    [navigate, location.search],
  );
}
